import { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { WorkoutService } from '../../../../lib/workoutService';
import { ExerciseService } from '../../../../lib/exerciseService';
import type { TrainerExerciseListRow } from '../../../../types/dataModel';
import {
  LIBRARY_EXERCISE_COUNT_CACHE_KEY,
  LIBRARY_WORKOUT_PLANS_COUNT_CACHE_KEY,
  LIBRARY_WORKOUTS_COUNT_CACHE_KEY,
  readCachedLibraryCount,
  writeCachedLibraryCount,
} from '../utils/libraryCountCache';
import { mergeWorkoutLibraryRowsFromServer } from '../utils/mergeWorkoutLibraryRows';
import { normalizeWorkoutSummaryRow } from '../utils/workoutLibraryRowUtils';

export type LibraryTab = 'workouts' | 'workoutPlans' | 'exercises';
export type LibraryLoadTarget = LibraryTab;

const LIBRARY_CACHE_KEY = 'trainer_library_cache_v3';
const LIBRARY_CACHE_MAX_AGE_MS = 5 * 60 * 1000;
const SKIP_LOAD_UNTIL_KEY = 'trainer_skip_library_load_until';

export type UseLibraryLoaderParams = {
  profileId: string | undefined;
  activeSection: string;
};

export function useLibraryLoader({ profileId, activeSection }: UseLibraryLoaderParams) {
  const [workouts, setWorkouts] = useState<any[]>([]);
  const [workoutPlans, setWorkoutPlans] = useState<any[]>([]);
  const [exercises, setExercises] = useState<TrainerExerciseListRow[]>([]);

  const workoutsCountSeedRef = useRef<{ count: number; loaded: boolean } | null>(null);
  if (workoutsCountSeedRef.current === null) {
    workoutsCountSeedRef.current = readCachedLibraryCount(LIBRARY_WORKOUTS_COUNT_CACHE_KEY);
  }
  const workoutPlansCountSeedRef = useRef<{ count: number; loaded: boolean } | null>(null);
  if (workoutPlansCountSeedRef.current === null) {
    workoutPlansCountSeedRef.current = readCachedLibraryCount(LIBRARY_WORKOUT_PLANS_COUNT_CACHE_KEY);
  }
  const exerciseCountSeedRef = useRef<{ count: number; loaded: boolean } | null>(null);
  if (exerciseCountSeedRef.current === null) {
    exerciseCountSeedRef.current = readCachedLibraryCount(LIBRARY_EXERCISE_COUNT_CACHE_KEY);
  }

  const [displayWorkoutsCount, setDisplayWorkoutsCount] = useState(workoutsCountSeedRef.current.count);
  const [displayWorkoutPlansCount, setDisplayWorkoutPlansCount] = useState(workoutPlansCountSeedRef.current.count);
  const [displayExercisesCount, setDisplayExercisesCount] = useState(exerciseCountSeedRef.current.count);
  const [workoutsCountLoaded, setWorkoutsCountLoaded] = useState(workoutsCountSeedRef.current.loaded);
  const [workoutPlansCountLoaded, setWorkoutPlansCountLoaded] = useState(workoutPlansCountSeedRef.current.loaded);
  const [exercisesCountLoaded, setExercisesCountLoaded] = useState(exerciseCountSeedRef.current.loaded);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [exerciseLoadError, setExerciseLoadError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<LibraryTab>(() => {
    try {
      const saved = localStorage.getItem('trainer_dashboard_active_tab');
      if (saved === 'workoutPlans' || saved === 'workouts' || saved === 'exercises') {
        return saved as LibraryTab;
      }
    } catch (error) {
      console.error('Error loading saved active tab:', error);
    }
    return 'exercises';
  });

  useEffect(() => {
    try {
      if (activeTab === 'workouts' || activeTab === 'workoutPlans' || activeTab === 'exercises') {
        localStorage.setItem('trainer_dashboard_active_tab', activeTab);
      }
    } catch (error) {
      console.error('Error saving active tab:', error);
    }
  }, [activeTab]);

  const getCachedLibraryData = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(LIBRARY_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        workouts?: any[];
        workoutPlans?: any[];
        exercises?: TrainerExerciseListRow[];
        timestamp?: number;
      };
      if (!parsed.timestamp || Date.now() - parsed.timestamp >= LIBRARY_CACHE_MAX_AGE_MS) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const workoutsRef = useRef(workouts);
  const workoutPlansRef = useRef(workoutPlans);
  const exercisesRef = useRef(exercises);
  workoutsRef.current = workouts;
  workoutPlansRef.current = workoutPlans;
  exercisesRef.current = exercises;

  const loadLibraryContent = useCallback(
    async (options?: { silent?: boolean; targets?: LibraryLoadTarget[] }) => {
      try {
        const silent = options?.silent === true;
        const targets = options?.targets ?? ['workouts', 'workoutPlans', 'exercises'];
        const shouldLoadWorkouts = targets.includes('workouts');
        const shouldLoadWorkoutPlans = targets.includes('workoutPlans');
        const shouldLoadExercises = targets.includes('exercises');
        const hasExistingData =
          (shouldLoadWorkouts && workoutsRef.current.length > 0) ||
          (shouldLoadWorkoutPlans && workoutPlansRef.current.length > 0) ||
          (shouldLoadExercises && exercisesRef.current.length > 0);
        if (!silent && !hasExistingData) setLoadingLibrary(true);

        const [allWorkouts, allPlans, allExercises] = await Promise.all([
          shouldLoadWorkouts ? WorkoutService.getWorkoutsSummary() : Promise.resolve(null),
          shouldLoadWorkoutPlans ? WorkoutService.getWorkoutPlans() : Promise.resolve(null),
          shouldLoadExercises && profileId
            ? ExerciseService.getTrainerExerciseList(profileId)
            : Promise.resolve(null),
        ]);

        let nextWorkouts = workoutsRef.current;
        if (shouldLoadWorkouts && allWorkouts) {
          if (!profileId) {
            nextWorkouts = [];
            setWorkouts([]);
          } else {
            const fromServer = allWorkouts.filter((w) => w.createdBy === profileId);
            flushSync(() => {
              setWorkouts((prev) => {
                nextWorkouts = mergeWorkoutLibraryRowsFromServer(fromServer, prev, profileId);
                return nextWorkouts;
              });
            });
          }
          setDisplayWorkoutsCount(nextWorkouts.length);
          setWorkoutsCountLoaded(true);
          writeCachedLibraryCount(LIBRARY_WORKOUTS_COUNT_CACHE_KEY, nextWorkouts.length);
        }

        let nextWorkoutPlans = workoutPlansRef.current;
        if (shouldLoadWorkoutPlans && allPlans) {
          const trainerPlans = allPlans.filter((p) => p.createdBy === profileId);
          nextWorkoutPlans = await Promise.all(
            trainerPlans.map(async (plan) => {
              try {
                const workoutCount = await WorkoutService.getPlanWorkoutCount(plan.id);
                return { ...plan, workoutCount };
              } catch (error) {
                console.error(`Error loading workout count for plan ${plan.id}:`, error);
                return { ...plan, workoutCount: 0 };
              }
            }),
          );
          setWorkoutPlans(nextWorkoutPlans);
          setDisplayWorkoutPlansCount(nextWorkoutPlans.length);
          setWorkoutPlansCountLoaded(true);
          writeCachedLibraryCount(LIBRARY_WORKOUT_PLANS_COUNT_CACHE_KEY, nextWorkoutPlans.length);
        }

        let nextExercises = exercisesRef.current;
        if (shouldLoadExercises && allExercises) {
          nextExercises = allExercises;
          setExercises(nextExercises);
          setDisplayExercisesCount(nextExercises.length);
          setExercisesCountLoaded(true);
          writeCachedLibraryCount(LIBRARY_EXERCISE_COUNT_CACHE_KEY, nextExercises.length);
        }

        const cached = getCachedLibraryData();
        try {
          sessionStorage.setItem(
            LIBRARY_CACHE_KEY,
            JSON.stringify({
              workouts: shouldLoadWorkouts ? nextWorkouts : (cached?.workouts ?? workoutsRef.current),
              workoutPlans: shouldLoadWorkoutPlans
                ? nextWorkoutPlans
                : (cached?.workoutPlans ?? workoutPlansRef.current),
              exercises: shouldLoadExercises ? nextExercises : (cached?.exercises ?? exercisesRef.current),
              timestamp: Date.now(),
            }),
          );
        } catch {
          /* ignore */
        }
      } catch (error) {
        console.error('Error loading library content:', error);
      } finally {
        setLoadingLibrary(false);
      }
    },
    [profileId, getCachedLibraryData],
  );

  const loadAllExercisesForLibrary = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!profileId) return;
      try {
        if (!options?.silent) setExerciseLoadError(null);
        const rows = await ExerciseService.getTrainerExerciseList(profileId);
        setExercises(rows);
        setDisplayExercisesCount(rows.length);
        setExercisesCountLoaded(true);
        writeCachedLibraryCount(LIBRARY_EXERCISE_COUNT_CACHE_KEY, rows.length);
      } catch (error) {
        console.error('loadAllExercisesForLibrary:', error);
        if (!options?.silent) {
          setExerciseLoadError(error instanceof Error ? error.message : 'Failed to load exercises.');
        }
      }
    },
    [profileId],
  );

  useEffect(() => {
    if (activeSection !== 'library' || !profileId) return;
    try {
      const skipUntil = sessionStorage.getItem(SKIP_LOAD_UNTIL_KEY);
      if (skipUntil) {
        const t = parseInt(skipUntil, 10);
        if (Date.now() < t) return;
        sessionStorage.removeItem(SKIP_LOAD_UNTIL_KEY);
      }
    } catch {
      /* ignore */
    }

    const cached = getCachedLibraryData();
    if (activeTab === 'workouts' && workouts.length === 0 && cached?.workouts) {
      const hydrated = Array.isArray(cached.workouts) ? cached.workouts.map(normalizeWorkoutSummaryRow) : [];
      setWorkouts(hydrated);
      setDisplayWorkoutsCount(hydrated.length);
      setWorkoutsCountLoaded(true);
      writeCachedLibraryCount(LIBRARY_WORKOUTS_COUNT_CACHE_KEY, hydrated.length);
    }
    if (activeTab === 'workoutPlans' && workoutPlans.length === 0 && cached?.workoutPlans) {
      const hydrated = Array.isArray(cached.workoutPlans) ? cached.workoutPlans : [];
      setWorkoutPlans(hydrated);
      setDisplayWorkoutPlansCount(hydrated.length);
      setWorkoutPlansCountLoaded(true);
      writeCachedLibraryCount(LIBRARY_WORKOUT_PLANS_COUNT_CACHE_KEY, hydrated.length);
    }
    if (activeTab === 'exercises' && exercises.length === 0 && cached?.exercises) {
      const hydrated = Array.isArray(cached.exercises) ? cached.exercises : [];
      setExercises(hydrated);
      setDisplayExercisesCount(hydrated.length);
      setExercisesCountLoaded(true);
      writeCachedLibraryCount(LIBRARY_EXERCISE_COUNT_CACHE_KEY, hydrated.length);
    }

    const target: LibraryLoadTarget =
      activeTab === 'workouts' ? 'workouts' : activeTab === 'workoutPlans' ? 'workoutPlans' : 'exercises';
    void loadLibraryContent({ targets: [target] });
  }, [activeSection, activeTab, profileId, getCachedLibraryData, loadLibraryContent, workouts.length, workoutPlans.length, exercises.length]);

  useEffect(() => {
    if (activeSection !== 'library' || !profileId) return;

    if (!workoutsCountLoaded) {
      WorkoutService.getWorkoutsCountForTrainer(profileId)
        .then((count) => {
          setDisplayWorkoutsCount(count);
          setWorkoutsCountLoaded(true);
          writeCachedLibraryCount(LIBRARY_WORKOUTS_COUNT_CACHE_KEY, count);
        })
        .catch((err) => console.error('[LibraryCount] Workouts HEAD fetch FAILED:', err));
    }
    if (!workoutPlansCountLoaded) {
      WorkoutService.getWorkoutPlansCountForTrainer(profileId)
        .then((count) => {
          setDisplayWorkoutPlansCount(count);
          setWorkoutPlansCountLoaded(true);
          writeCachedLibraryCount(LIBRARY_WORKOUT_PLANS_COUNT_CACHE_KEY, count);
        })
        .catch((err) => console.error('[LibraryCount] Workout Plans HEAD fetch FAILED:', err));
    }
    if (!exercisesCountLoaded) {
      ExerciseService.getExercisesCountForTrainer(profileId)
        .then((count) => {
          setDisplayExercisesCount(count);
          setExercisesCountLoaded(true);
          writeCachedLibraryCount(LIBRARY_EXERCISE_COUNT_CACHE_KEY, count);
        })
        .catch((err) => console.error('[LibraryCount] Exercises HEAD fetch FAILED:', err));
    }
  }, [activeSection, profileId, workoutsCountLoaded, workoutPlansCountLoaded, exercisesCountLoaded]);

  const libTabCount = useCallback(
    (tab: LibraryTab): string => {
      if (tab === 'workouts') {
        return workoutsCountLoaded ? String(workouts.length || displayWorkoutsCount) : String(displayWorkoutsCount);
      }
      if (tab === 'workoutPlans') {
        return workoutPlansCountLoaded
          ? String(workoutPlans.length || displayWorkoutPlansCount)
          : String(displayWorkoutPlansCount);
      }
      return exercisesCountLoaded ? String(exercises.length || displayExercisesCount) : String(displayExercisesCount);
    },
    [
      workouts.length,
      workoutPlans.length,
      exercises.length,
      workoutsCountLoaded,
      workoutPlansCountLoaded,
      exercisesCountLoaded,
      displayWorkoutsCount,
      displayWorkoutPlansCount,
      displayExercisesCount,
    ],
  );

  /** Hydrate lists from session cache (e.g. return from exercise detail). */
  const hydrateListsFromSessionCache = useCallback(() => {
    const cached = getCachedLibraryData();
    if (!cached) return false;
    if (Array.isArray(cached.workouts)) {
      setWorkouts(cached.workouts.map(normalizeWorkoutSummaryRow));
      setDisplayWorkoutsCount(cached.workouts.length);
      setWorkoutsCountLoaded(true);
      writeCachedLibraryCount(LIBRARY_WORKOUTS_COUNT_CACHE_KEY, cached.workouts.length);
    }
    if (Array.isArray(cached.workoutPlans)) {
      setWorkoutPlans(cached.workoutPlans);
      setDisplayWorkoutPlansCount(cached.workoutPlans.length);
      setWorkoutPlansCountLoaded(true);
      writeCachedLibraryCount(LIBRARY_WORKOUT_PLANS_COUNT_CACHE_KEY, cached.workoutPlans.length);
    }
    if (Array.isArray(cached.exercises)) {
      setExercises(cached.exercises);
      setDisplayExercisesCount(cached.exercises.length);
      setExercisesCountLoaded(true);
      writeCachedLibraryCount(LIBRARY_EXERCISE_COUNT_CACHE_KEY, cached.exercises.length);
    }
    try {
      sessionStorage.setItem(SKIP_LOAD_UNTIL_KEY, String(Date.now() + 3000));
    } catch {
      /* ignore */
    }
    return true;
  }, [getCachedLibraryData]);

  return {
    activeTab,
    setActiveTab,
    workouts,
    setWorkouts,
    workoutPlans,
    setWorkoutPlans,
    exercises,
    setExercises,
    loadingLibrary,
    exerciseLoadError,
    setExerciseLoadError,
    loadLibraryContent,
    loadAllExercisesForLibrary,
    libTabCount,
    hydrateListsFromSessionCache,
    LIBRARY_CACHE_KEY,
    LIBRARY_CACHE_MAX_AGE_MS,
  };
}

export type LibraryLoaderApi = ReturnType<typeof useLibraryLoader>;
